import { Injectable } from '@angular/core';
import $ from 'jquery';

import { FieldType } from '@sb-tools/typescript/enums/field-type.enum';
import { Sentiments } from '../enums/sentiments.enum';
import { HelperService } from '@sb-tools/typescript/services/helper.service';
import { Field } from '@sb-tools/typescript/models/field.model';
import { ClientField } from '../models/client-field.model';

@Injectable({
    providedIn: 'root',
})
export class FormFieldsValidationService {
    sentimentsEnum = Sentiments;

    checkAndSetValidValues(field: Field | ClientField, checkedValue: string): void {
        switch (field.type) {
            case FieldType.accountNumber: {
                if (this.isAccountNumberValid(checkedValue)) {
                    field.value = checkedValue;
                }
                break;
            }
            case FieldType.address: {
                const addressValueArray = checkedValue.split(',');
                const zipValue = addressValueArray[0];
                const addressValue = addressValueArray[1];
                field.valueModel = {};

                if (!isNaN(+zipValue)) {
                    field.valueModel.zipCode = zipValue.trim();
                }

                if (addressValue) {
                    field.valueModel.address = addressValue.trim();
                }

                if (field.valueModel.zipCode || field.valueModel.address) {
                    field.value =
                        (field.valueModel.zipCode || '') +
                            (field.valueModel.address ? ', ' : '') +
                            field.valueModel.address || '';
                }

                break;
            }
            case FieldType.attachment:
            case FieldType.storeInfo:
            case FieldType.formHeader: {
                break;
            }
            case FieldType.sentiment: {
                const isValueCorrect = checkedValue.toString().toLowerCase() in this.sentimentsEnum;

                if (!isValueCorrect) {
                    field.value = this.sentimentsEnum.question;
                } else {
                    if (isNaN(+checkedValue)) {
                        field.value = this.sentimentsEnum[checkedValue.toLowerCase()];
                    } else {
                        field.value = +checkedValue;
                    }
                }

                break;
            }
            case FieldType.select:
            case FieldType.region:
            case FieldType.checklist:
            case FieldType.radio: {
                const correctValuesList = checkedValue.split(', ').filter((value) => {
                    return this.isValueInPossibleValuesList(field, value);
                });

                if (correctValuesList.length) {
                    field.value = correctValuesList.join(', ');
                }

                break;
            }
            case FieldType.checkbox: {
                if (['false', 'true'].includes(checkedValue)) {
                    field.value = !(checkedValue === 'false');
                }
                break;
            }
            case FieldType.emailInput:
            case FieldType.outboundEmailAddress: {
                if (HelperService.isEmailValid(checkedValue)) {
                    field.value = checkedValue;
                }

                break;
            }
            case FieldType.mobileNumber: {
                if (this.isMobileNumberValid(checkedValue)) {
                    field.value = checkedValue;
                }
                break;
            }
            case FieldType.numberInput: {
                if (this.isNumberValueValid(field, checkedValue)) {
                    field.value = checkedValue;
                }
                break;
            }
            case FieldType.textInput: {
                if (this.isTextInputValueValid(field, checkedValue)) {
                    field.value = checkedValue;
                }
                break;
            }
            case FieldType.starRating: {
                if (this.isStarRatingValueValid(field, checkedValue)) {
                    field.value = checkedValue;
                }
                break;
            }
            default: {
                field.value = checkedValue;
            }
        }
    }

    isAccountNumberValid(value): boolean {
        // example of valid account: 97135974068
        const weights = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
        const numberWithoutSpacesAndPeriods = value.replace(/[\s.]+/g, '');

        if (numberWithoutSpacesAndPeriods.length !== 11) {
            return false;
        } else {
            const checkDigit = parseInt(numberWithoutSpacesAndPeriods.charAt(10), 10);
            const accountNumberWithoutCheckDigit = numberWithoutSpacesAndPeriods.substring(0, 10);
            let sum = 0;

            for (let index = 0; index < 10; index++) {
                sum += parseInt(accountNumberWithoutCheckDigit.charAt(index), 10) * weights[index];
            }
            const remainder = sum % 11;
            return checkDigit === (remainder === 0 ? 0 : 11 - remainder);
        }
    }

    isRequiredValuesFilled(fields): boolean {
        const checkedFormElementTypes = [
            FieldType.attachment,
            FieldType.categories,
            FieldType.checklist,
            FieldType.date,
            FieldType.ccUsers,
            FieldType.bccUsers,
        ];

        let isValid = true;
        checkedFormElementTypes.forEach((type) => {
            fields.find((formField) => {
                if (formField.type === type && this.isNeedToCheckFieldValue(formField)) {
                    if (formField.type === FieldType.categories) {
                        isValid = !formField.isRequired || !!formField.value;
                    } else if (formField.isRequired && !(formField.value && formField.value.length)) {
                        isValid = false;
                    }
                }
            });
        });

        return isValid;
    }

    isValueInPossibleValuesList(formField: Field | ClientField, value: string): boolean {
        return !!formField.possibleValues.find((possibleValue) => {
            return possibleValue.value === value;
        });
    }

    scrollToInvalidField(formIdSelector: string, scrolledContainerIdSelector: string, offsetTop = 0): void {
        if ($(formIdSelector + ' .ng-invalid').first().length) {
            $(scrolledContainerIdSelector).animate(
                {
                    scrollTop: $(formIdSelector + ' .ng-invalid').first()[0].offsetTop - 30 - offsetTop,
                },
                'slow',
            );

            if (
                $(formIdSelector + ' .ng-invalid')
                    .first()
                    .find('.quill-editor.ng-invalid').length
            ) {
                $('.quill-editor.ng-invalid .ql-editor').focus();
                return;
            }

            if (
                $(formIdSelector + ' .ng-invalid')
                    .first()
                    .find('.form-control.ng-invalid').length
            ) {
                $(formIdSelector + ' .ng-invalid')
                    .first()
                    .find('.form-control.ng-invalid')[0]
                    .focus();
                return;
            }

            if (
                $(formIdSelector + ' .ng-invalid')
                    .first()
                    .find('.bs-timepicker-field').length
            ) {
                $(formIdSelector + ' .ng-invalid')
                    .first()
                    .find('.bs-timepicker-field')[0]
                    .focus();
                return;
            }
        }
    }

    private isMobileNumberValid(value): boolean {
        return /^(\+)?[0-9]{4,13}$/.test(value);
    }

    private isNeedToCheckFieldValue(formField: ClientField): boolean {
        return !formField.isDependant || (formField.isDependant && formField.isShowedBecauseOfRule);
    }

    private isNumberValueValid(field, value): boolean {
        const valueWithoutComma = value.replace(/,/g, '.');
        if (isNaN(Number(valueWithoutComma)) || isNaN(parseFloat(valueWithoutComma))) {
            return false;
        }

        const maxLength = field.options?.maxNumber ? +field.options?.maxNumber : Number.MAX_VALUE;
        if (value.length > maxLength) {
            // value = value.slice(0, maxLength)
            return false;
        }

        const minLength = field.options?.minNumber != null ? +field.options?.maxNumber : 0;
        if (value.length < minLength) {
            return false;
        }

        return true;
    }

    private isTextInputValueValid(field, value): boolean {
        let isValid = false;

        if (field.options.pattern) {
            const pattern = new RegExp(field.options.pattern);
            isValid = pattern.test(value);
        } else {
            isValid = true;
        }

        return isValid;
    }
    private isStarRatingValueValid(field, value): boolean {
        return value > 0 && value <= field.options.maxStarCount;
    }
}
